/**
 * Apex Puzzle — The Orrery.
 *
 * Gates on all four prior puzzles being solved (read via
 * `ctx.levelState`). After Basil tunes the three lens rings, Minna and
 * Basil each latch one of the two sync pressure plates (in any order) to
 * activate the orrery, which powers up the exit portal.
 *
 * This module also owns the exit portal because the portal's open/closed
 * state is tied to the orrery activation.
 *
 * When the portal is entered it emits `PORTAL_ENTERED` which the scene
 * shell listens to in order to start the Elder encounter — the orrery
 * puzzle intentionally does not trigger the cutscene itself.
 */
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { PointLight } from "@babylonjs/core/Lights/pointLight";

import { PressurePlate } from "../../../entities/interactives/PressurePlate";
import { Portal } from "../../../entities/interactives/Portal";
import { Dial } from "../../../entities/interactives/Dial";

import { GameEvents } from "../../../config/events";
import { CLOCKWORK_CANOPY } from "../../../config/GameConfig";
import { tagAs } from "../../PlaceholderTag";
import { getAssetManager, InstantiatedModel } from "../../../systems/AssetManager";

import type { PuzzleContext, PuzzleCue, PuzzleModule, PuzzleState } from "../../PuzzleModule";
import type { CueDef } from "../../../ui/tutorialCues";
import type { PuzzleQAHints } from "../../qa/PuzzleQAHints";

/** Puzzle IDs that the apex gates on. Must all report `solved: true`. */
const REQUIRED_PUZZLES = [
  "gearAlignment",
  "lightPath",
  "timedResonance",
  "shadowRelay",
] as const;

const LENS_INTERACTION_RADIUS = 3.35;

const CUE = {
  grip: { cat: "basil", parts: [{ action: "turnStart" }, { text: "grip the lens ring" }] },
  spin: { cat: "basil", parts: [{ action: "rhythmHit" }, { text: "on the beat" }] },
  plates: { cat: "both", parts: [{ text: "one cat on each sync plate" }] },
  portal: { cat: "both", parts: [{ text: "into the portal together" }] },
} satisfies Record<string, CueDef>;
const OBJECTIVE = "Awaken the Orrery";

export class TheOrreryPuzzle implements PuzzleModule {
  readonly id = "theOrrery";
  readonly displayName = "The Orrery";

  private ctx!: PuzzleContext;

  private orreryTorus!: Mesh;
  private orreryMat!: PBRMaterial;
  private orrerySphere!: Mesh;
  private sphereMat!: PBRMaterial;
  private syncPlateA!: PressurePlate;
  private syncPlateB!: PressurePlate;
  private exitPortal!: Portal;

  /**
   * Canon fidelity (book p.25–26): the Orrery is a lens + star-chart
   * alignment puzzle, not a pressure-plate gate. Three procedural lens
   * rings use the existing `Dial` interactive — each must be rotated
   * to its target notch (the "star chart angle") before the sync plates
   * come alive. Minna's Aura Sight reveals the rings as interactives;
   * Basil's Turn-Table Touch spins them.
   */
  private lensRings: Dial[] = [];
  private lensesReady = false;

  // Basil's Turn-Table Touch target tracking for the lens rings. Only one
  // ring at a time can be the active rhythm target — `turnTableTarget`
  // holds the ring's key ("lensRingAlpha" | "lensRingBeta" | "lensRingGamma")
  // so the shared TURN_TABLE_TOUCH_HIT listener knows which Dial to spin.
  // Mirrors the same pattern used in GearAlignmentPuzzle.
  private turnTableActive = false;
  private turnTableTarget: string | null = null;

  /**
   * Visible brass orrery prop loaded via Meshy. Parented to the scene
   * at the torus/sphere position so it sits ON the platform and gives
   * the apex puzzle physical presence. The procedural torus and sphere
   * stay in place underneath as a deterministic fallback and as the
   * activation-state indicator (emissive brightens when solved).
   */
  private orreryProp: InstantiatedModel | null = null;

  /**
   * Constellation accent lights we brighten when the orrery activates.
   * The level shell creates them and passes them in because they are
   * part of the background lighting rig, not apex-puzzle-specific meshes.
   */
  private constellationLights: PointLight[];

  private syncA = false;
  private syncB = false;
  /** Which cats have latched each sync plate (see the sync plate events). */
  private syncACats = new Set<"minna" | "basil">();
  private syncBCats = new Set<"minna" | "basil">();
  private activated = false;

  constructor(constellationLights: PointLight[]) {
    this.constellationLights = constellationLights;
  }

  private async loadOrreryProp(): Promise<void> {
    // Canon astrolabe: three concentric brass/copper rings on tilted
    // axes + emissive core sphere + equatorial bar + pedestal. Reads as
    // "armillary sphere that maps the sky" rather than "a torus on a
    // platform." Authored in Blender, ~569 KB.
    try {
      const instance = await getAssetManager().instantiateSharedModel(
        this.ctx.scene,
        "/assets/models/environment/orrery_astrolabe.glb",
      );
      this.orreryProp = instance;
      for (const root of instance.rootNodes) {
        if ("position" in root && "scaling" in root) {
          (root as { position: Vector3; scaling: Vector3 }).position =
            new Vector3(125, 10, 0);
          (root as { position: Vector3; scaling: Vector3 }).scaling =
            new Vector3(1.0, 1.0, 1.0);
        }
      }
      for (const mesh of instance.meshes) {
        mesh.isPickable = false;
      }
    } catch (e) {
      console.warn("[TheOrrery] Failed to load orrery_astrolabe.glb:", e);
    }
  }

  setup(ctx: PuzzleContext): void {
    this.ctx = ctx;
    const { scene } = ctx;

    // ── Orrery structure: large brass torus + sphere ──
    this.orreryTorus = MeshBuilder.CreateTorus(
      "orreryTorus",
      { diameter: 8, thickness: 0.5, tessellation: 48 },
      scene,
    );
    this.orreryTorus.position = new Vector3(125, 10, 0);
    this.orreryMat = new PBRMaterial("orreryMat", scene);
    this.orreryMat.albedoColor = Color3.FromHexString(
      CLOCKWORK_CANOPY.COLORS.ORRERY_BRASS,
    );
    this.orreryMat.metallic = 0.9;
    this.orreryMat.roughness = 0.15;
    this.orreryMat.emissiveColor = new Color3(0.02, 0.015, 0.005);
    this.orreryTorus.material = this.orreryMat;

    this.orrerySphere = MeshBuilder.CreateSphere(
      "orrerySphere",
      { diameter: 2.5, segments: 24 },
      scene,
    );
    this.orrerySphere.position = new Vector3(125, 10, 0);
    this.sphereMat = new PBRMaterial("sphereMat", scene);
    this.sphereMat.albedoColor = new Color3(0.15, 0.12, 0.25);
    this.sphereMat.metallic = 0.4;
    this.sphereMat.roughness = 0.3;
    this.sphereMat.emissiveColor = new Color3(0.01, 0.01, 0.04);
    this.orrerySphere.material = this.sphereMat;

    // Orrery torus + sphere are procedural placeholders that stay in
    // place as the activation-state indicator (emissive brightens on
    // solve). The visible hero prop is layered on top via a Meshy GLB.
    tagAs(this.orreryTorus, "placeholder");
    tagAs(this.orrerySphere, "placeholder");

    // Fire-and-forget load of the Meshy-generated brass orrery prop.
    // Parented to the scene at the orrery center (125, 5, 0) with a
    // modest scale so it sits on the platform. If the asset is missing
    // the load fails silently and the procedural torus/sphere remain.
    void this.loadOrreryProp();

    // ── Lens rings (canon lens + star-chart alignment) ──
    // Three rings arranged in a wide arc beneath the orrery body.
    // Target notches are deliberately asymmetric so there's no "just
    // hit the top" solution — each lens reads a different star chart.
    const lensSpecs: { key: string; x: number; notches: number; target: number; label: string }[] = [
      { key: "lensRingAlpha",  x: 117, notches: 8, target: 2, label: "Lens Ring α (Lyra)" },
      { key: "lensRingBeta",   x: 125, notches: 8, target: 5, label: "Lens Ring β (Draco)" },
      { key: "lensRingGamma",  x: 133, notches: 8, target: 7, label: "Lens Ring γ (Orion)" },
    ];
    for (const spec of lensSpecs) {
      const lens = new Dial(scene, {
        key: spec.key,
        position: new Vector3(spec.x, 6.6, 0),
        label: spec.label,
        size: new Vector3(1.4, 0.18, 1.4),
        totalNotches: spec.notches,
        targetNotch: spec.target,
      });
      this.lensRings.push(lens);
    }

    // ── Sync plates ──
    // Either cat may press either plate, and a pressed plate LATCHES
    // (same pattern as ShadowRelay's relayPlate). The plates were
    // originally cat-specific (A read only Minna, B only Basil) and
    // required simultaneous same-frame pressure — but the plates sit
    // 10u apart and the solo-mode idle cat auto-follows its partner
    // when they separate beyond 6u, so a solo player could never hold
    // both at once: the apex was a hard stop for anyone playing alone.
    // Latching makes the plates a sequence instead of a stunt; each plate
    // latches for the cat standing on it, and the orrery needs the two
    // plates latched by DIFFERENT cats. (Latching alone let one cat press
    // plate A, walk to plate B, and wake the orrery solo — PUZZLE_SOLVED
    // fires here, before the two-cat portal; see
    // tools/playtest/scenarios/solo-solve.mjs.)
    this.syncPlateA = new PressurePlate(scene, {
      key: "syncPlateA",
      position: new Vector3(120, 5.0, 0),
      label: "West Sync Plate",
    });
    this.syncPlateB = new PressurePlate(scene, {
      key: "syncPlateB",
      position: new Vector3(130, 5.0, 0),
      label: "East Sync Plate",
    });

    // ── Exit portal ──
    // Entry zone widened from the ±0.75u default: two cat capsules
    // physically collide inside a 1.5u window, and the solo idle
    // follower stops ~1.5u short of the active cat — both made the
    // finale walk-in needlessly fiddly (or impossible solo). ±2.2u
    // horizontal comfortably seats both cats without changing the arch.
    this.exitPortal = new Portal(scene, {
      key: "canopyPortal",
      position: new Vector3(135, 5.3, 0),
      label: "Portal to the Stars",
      entryHalfExtents: { x: 2.2, y: 1.6 },
    });

    ctx.registerInteractive({
      mesh: this.syncPlateA.rootMesh,
      hidden: false,
      state: "revealed",
    });
    ctx.registerInteractive({
      mesh: this.syncPlateB.rootMesh,
      hidden: false,
      state: "revealed",
    });
    ctx.registerInteractive({
      mesh: this.exitPortal.rootMesh,
      hidden: false,
      state: "revealed",
    });
    // Register the lens rings so Aura Sight reveals them with the
    // canonical glow and the HUD prompt system picks them up.
    for (const lens of this.lensRings) {
      ctx.registerInteractive({
        mesh: lens.rootMesh,
        hidden: false,
        state: "revealed",
      });
    }

    // ── Sync plate events ──
    // Latching: PRESSURE_PLATE_DEACTIVATED is deliberately not handled —
    // once a cat has pressed a plate it stays synced for that cat (see
    // plate comment). The press is credited to whichever cat stands on
    // the plate when it goes down (the event fires from checkPressure in
    // update(), with the cats where they are).
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.PRESSURE_PLATE_ACTIVATED, (key) => {
        if (key !== "syncPlateA" && key !== "syncPlateB") return;
        const plate = key === "syncPlateA" ? this.syncPlateA : this.syncPlateB;
        const latched = key === "syncPlateA" ? this.syncACats : this.syncBCats;
        const before = latched.size;
        if (plate.isOccupiedBy(this.ctx.minna.mesh.position)) latched.add("minna");
        if (plate.isOccupiedBy(this.ctx.basil.mesh.position)) latched.add("basil");
        this.syncA = this.syncACats.size > 0;
        this.syncB = this.syncBCats.size > 0;
        if (latched.size > before && !this.syncArmed()) {
          const bothPlatesOneCat = this.syncA && this.syncB;
          ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
            message: bothPlatesOneCat
              ? "Both sync plates answered to the same cat. Minna and Basil must each hold one."
              : key === "syncPlateA"
                ? "West sync plate locked in — the east plate still waits."
                : "East sync plate locked in — the west plate still waits.",
            color: CLOCKWORK_CANOPY.COLORS.LENS_GOLD,
          });
        }
        this.checkActivation();
      }),
    );

    // ── Lens ring events ──
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.DIAL_ALIGNED, () => this.checkLensAlignment()),
    );
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.DIAL_TURNED, () => this.checkLensAlignment()),
    );

    // Turn-Table Touch rhythm hit → spin the active lens ring. The shared
    // TURN_TABLE_TOUCH_HIT event is broadcast to every listener, so we
    // filter on `turnTableTarget` to make sure only the puzzle that
    // activated TT Touch consumes the hit.
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.TURN_TABLE_TOUCH_HIT, () => {
        if (!this.turnTableActive || !this.turnTableTarget) return;
        const ring = this.lensRings.find((l) => l.key === this.turnTableTarget);
        if (ring) ring.spin();
      }),
    );
  }

  private checkLensAlignment(): void {
    if (this.lensesReady) return;
    let alignedCount = 0;
    for (const lens of this.lensRings) {
      if (!lens.aligned) continue;
      alignedCount++;
      if (!lens.locked) lens.lock();
    }
    const allAligned = alignedCount === this.lensRings.length;
    if (!allAligned) return;
    this.lensesReady = true;
    this.ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
      message: "Lenses locked. Stand on the sync plates to awaken the Orrery.",
      color: CLOCKWORK_CANOPY.COLORS.LENS_GOLD,
    });
    // Brighten the orrery sphere as a visible milestone.
    this.sphereMat.emissiveColor = Color3.FromHexString(
      CLOCKWORK_CANOPY.COLORS.CONSTELLATION,
    ).scale(0.2);
    // Re-check in case both cats are already on the sync plates.
    this.checkActivation();
  }

  update(dt: number, _elapsed: number): void {
    const { minna, basil } = this.ctx;

    // Sync plates — either cat can press either plate; each press is
    // credited to the cat standing on it (see the sync plate events).
    this.syncPlateA.checkPressure([minna.mesh, basil.mesh]);
    this.syncPlateB.checkPressure([minna.mesh, basil.mesh]);

    // Lens ring proximity highlight — only while the apex hasn't activated
    // and while the individual ring still needs alignment. Auto-deactivate
    // TT Touch if Basil walks away from the currently targeted ring.
    if (!this.activated) {
      const basilPos = basil.mesh.position;
      for (const lens of this.lensRings) {
        const dist = Vector3.Distance(basilPos, lens.rootMesh.position);
        if (dist < LENS_INTERACTION_RADIUS && !lens.aligned) lens.highlight();
        else lens.unhighlight();
      }
      if (this.turnTableActive && this.turnTableTarget) {
        const activeRing = this.lensRings.find(
          (l) => l.key === this.turnTableTarget,
        );
        if (activeRing) {
          const distToTarget = Vector3.Distance(
            basilPos,
            activeRing.rootMesh.position,
          );
          if (distToTarget >= LENS_INTERACTION_RADIUS) {
            this.ctx.basilAbilities.deactivateAbility("turnTableTouch");
            this.turnTableActive = false;
            this.turnTableTarget = null;
          }
        }
      }
    }

    // Portal entry (only tracked once the orrery is alive)
    if (this.activated) {
      this.exitPortal.checkEntry(minna.mesh.position, basil.mesh.position);
    }

    // Orrery visual: slow rotation when activated
    if (this.activated) {
      this.orreryTorus.rotation.z += 0.3 * dt;
      this.orreryTorus.rotation.x += 0.1 * dt;
      this.orrerySphere.rotation.y += 0.5 * dt;
    }
  }

  onAbilityKey(key: string, pressed: boolean, _released: boolean): boolean {
    // The orrery is the apex — once activated no more ring spinning.
    if (this.activated) return false;

    // 1 = toggle Turn-Table Touch on the closest lens ring.
    if (key === "1" && pressed) {
      const basilPos = this.ctx.basil.mesh.position;

      // Find the nearest lens ring within range that still needs alignment.
      let targetRing: Dial | null = null;
      let bestDist = Infinity;
      for (const lens of this.lensRings) {
        if (lens.aligned) continue;
        const dist = Vector3.Distance(basilPos, lens.rootMesh.position);
        if (dist < LENS_INTERACTION_RADIUS && dist < bestDist) {
          bestDist = dist;
          targetRing = lens;
        }
      }

      if (!targetRing) {
        // Basil isn't near any lens ring — let other puzzles handle "1".
        return false;
      }

      if (!this.turnTableActive || this.turnTableTarget !== targetRing.key) {
        // Switching targets (or activating fresh): deactivate first so the
        // AbilitySystem cleanly resets the active-ability slot.
        if (this.turnTableActive) {
          this.ctx.basilAbilities.deactivateAbility("turnTableTouch");
          // Same-press target switch: deactivate() just armed the 0.2s
          // cooldown, which would make the holder refuse the immediate
          // reactivation below. Zero it on this explicit switch path only
          // (same mechanism as GearAlignment) — holder-visible and
          // event-clean, with a matched DEACTIVATED → ACTIVATED pair.
          this.ctx.turnTableTouch.cooldownRemaining = 0;
        }
        // Route through the holder so ABILITY_ACTIVATED fires for
        // HUD/telemetry/banter; latch state on the actual result so
        // turnTableActive can no longer lie true on a refusal.
        this.ctx.turnTableTouch.setTarget(
          targetRing.rootMesh.position,
          (_combo) => {},
        );
        this.turnTableActive =
          this.ctx.basilAbilities.activateAbility("turnTableTouch");
        this.turnTableTarget = this.turnTableActive ? targetRing.key : null;
      } else {
        this.ctx.basilAbilities.deactivateAbility("turnTableTouch");
        this.turnTableActive = false;
        this.turnTableTarget = null;
      }
      return true;
    }

    // 2 = Turn-Table tap. Only an on-beat hit turns the ring: the hit
    // flows through TURN_TABLE_TOUCH_HIT (listener above). A miss does
    // nothing here — TurnTableTouch already applies the miss penalty —
    // so mashing 2 can't brute-force the lenses.
    if (key === "2" && pressed && this.turnTableActive && this.turnTableTarget) {
      const basilPos = this.ctx.basil.mesh.position;
      const activeRing = this.lensRings.find(
        (l) => l.key === this.turnTableTarget,
      );
      if (!activeRing) return false;
      const dist = Vector3.Distance(basilPos, activeRing.rootMesh.position);
      if (dist >= LENS_INTERACTION_RADIUS) {
        this.ctx.basilAbilities.deactivateAbility("turnTableTouch");
        this.turnTableActive = false;
        this.turnTableTarget = null;
        return false;
      }
      this.ctx.turnTableTouch.attemptHit();
      return true;
    }

    return false;
  }

  getCue(): PuzzleCue | null {
    if (this.activated) return null;
    const above = (v: Vector3, dy = 1.4) => ({ x: v.x, y: v.y + dy, z: 0 });
    if (!this.lensesReady) {
      const active = this.turnTableActive ? this.lensRings.find((l) => l.key === this.turnTableTarget) : undefined;
      if (active) return { objective: OBJECTIVE, cue: CUE.spin, anchor: above(active.rootMesh.position) };
      const next = this.lensRings.find((l) => !l.aligned);
      if (next) return { objective: OBJECTIVE, cue: CUE.grip, anchor: above(next.rootMesh.position) };
    }
    // Per-cat latches: Minna holds one plate, Basil the other.
    const plate = this.syncACats.size === 0 ? this.syncPlateA : this.syncPlateB;
    return { objective: OBJECTIVE, cue: CUE.plates, anchor: above(plate.rootMesh.position, 1.2) };
  }

  /** After the Orrery wakes (puzzle solved), the last step: the portal. */
  getPortalCue(): PuzzleCue | null {
    if (!this.activated) return null;
    return { objective: "Into the portal together", cue: CUE.portal, anchor: { x: 135, y: 7.4, z: 0 } };
  }

  getState(): PuzzleState {
    if (this.activated) return { solved: true, progress: 1 };
    // Weighted progress: 60% prior puzzles, 25% lens alignment, 15% sync plates.
    const priorSolved = REQUIRED_PUZZLES.filter(
      (id) => this.ctx?.levelState.get(id)?.solved,
    ).length;
    const alignedLenses = this.lensRings.filter((l) => l.aligned).length;
    const priorPortion = (priorSolved / REQUIRED_PUZZLES.length) * 0.6;
    const lensPortion = (alignedLenses / Math.max(1, this.lensRings.length)) * 0.25;
    const syncPortion = ((this.syncA ? 0.5 : 0) + (this.syncB ? 0.5 : 0)) * 0.15;
    return {
      solved: false,
      progress: Math.min(0.99, priorPortion + lensPortion + syncPortion),
    };
  }

  getQAHints(): PuzzleQAHints {
    return {
      puzzleId: this.id,
      successEvent: GameEvents.PUZZLE_SOLVED,
      targets: [
        { id: "orrery_core",    position: { x: 125, y: 10 },  radius: 2.0, allowedCat: "either", label: "Orrery centre (torus + sphere)" },
        { id: "orrery_pad_0",   position: { x: 120, y: 5.0 }, radius: 1.0, allowedCat: "minna", label: "Orrery pressure pad 0 (sync plate A)" },
        { id: "orrery_pad_1",   position: { x: 130, y: 5.0 }, radius: 1.0, allowedCat: "basil", label: "Orrery pressure pad 1 (sync plate B)" },
        { id: "orrery_pad_2",   position: { x: 135, y: 5.3 }, radius: 1.0, allowedCat: "either", label: "Orrery portal platform" },
        // Lens rings — Basil's TT Touch targets. Positions at y=6.6
        // (canopy specs above pressure pads).
        { id: "lens_alpha",     position: { x: 117, y: 6.6 }, radius: LENS_INTERACTION_RADIUS, allowedCat: "basil", label: "Lens ring α" },
        { id: "lens_beta",      position: { x: 125, y: 6.6 }, radius: LENS_INTERACTION_RADIUS, allowedCat: "basil", label: "Lens ring β" },
        { id: "lens_gamma",     position: { x: 133, y: 6.6 }, radius: LENS_INTERACTION_RADIUS, allowedCat: "basil", label: "Lens ring γ" },
        { id: "lens_alpha_staging", position: { x: 116.4, y: 3.9 }, radius: 0.8, allowedCat: "basil", label: "Basil staging for lens ring α" },
        { id: "lens_beta_staging",  position: { x: 122.3, y: 5.0 }, radius: 0.9, allowedCat: "basil", label: "Basil staging for lens ring β" },
        { id: "lens_gamma_staging", position: { x: 130.6, y: 5.0 }, radius: 0.9, allowedCat: "basil", label: "Basil staging for lens ring γ" },
        { id: "plat_s5_2_top",  position: { x: 120, y: 5.0 }, radius: 1.5, allowedCat: "either", label: "plat_s5_2 top (under lens_alpha + pad_0)" },
        { id: "plat_s5_3_top",  position: { x: 130, y: 5.0 }, radius: 1.5, allowedCat: "either", label: "plat_s5_3 top (under lens_beta + pad_1)" },
      ],
      traversals: [
        // Legacy Basil OvLeap fallback from plat_s5_1 to plat_s5_2.
        // The layout is now normal-jump reachable for both cats, but
        // the recipe remains useful for older traces and stress runs.
        {
          id: "basil_ovleap_to_plat_s5_2",
          kind: "ovLeap",
          cat: "basil",
          from: "lens_alpha",
          to: "plat_s5_2_top",
          successWindowMs: 5_000,
          recipe: [
            { key: "4", holdMs: 100 },
            { waitMs: 180 },
            { keyDown: "d" },
            { waitMs: 900 },
            { keyUp: "d" },
          ],
        },
        // Walk-jump from plat_s5_2 (top y=5.0) to plat_s5_3 (top y=5.0).
        // Same height; gap from plat_s5_2 east (x=122.5) to plat_s5_3
        // west (x=127.5) is 5u — running jump territory.
        {
          id: "running_jump_plat_s5_2_to_s5_3",
          kind: "jump",
          cat: "either",
          from: "plat_s5_2_top",
          to: "plat_s5_3_top",
          successWindowMs: 4_000,
          recipe: [
            { keyDown: "d" },
            { waitMs: 800 },
            { keyDown: " " },
            { waitMs: 80 },
            { keyUp: " " },
            { waitMs: 1_000 },
            { keyUp: "d" },
          ],
        },
      ],
    };
  }

  /**
   * Sub-puzzle state seeder. Shape:
   *   {
   *     lensesAligned?: boolean,   // mark all three lens rings aligned
   *     solved?: boolean,          // full activate() — opens portal + lights
   *   }
   *
   * The lens-alignment step is the single hardest beat in the apex
   * (three separate Dial sequences, each on a lens ring above a platform
   * traversal). `{ lensesAligned: true }` stages "now stand on the sync
   * plates" which is the only remaining manual input. `{ solved: true }`
   * skips straight to the portal-open state for traces testing the
   * Elder/portal-entered handshake.
   *
   * Note: `solved: true` bypasses the apex REQUIRED_PUZZLES gate, which
   * is fine — the QA harness uses `forceSolvePuzzle` on prior puzzles
   * separately, or this seed is only called when testing the apex in
   * isolation.
   */
  seed(state: Record<string, unknown>): void {
    if (state.lensesAligned === true && !this.lensesReady) {
      // Mark each ring aligned by spinning it until aligned so the Dial's
      // own state (pointer rotation, emissive, `aligned` getter) matches
      // what a real rhythm-complete sequence produces. Capped at totalNotches
      // spins as a safety so a misconfigured dial can't loop forever.
      for (const lens of this.lensRings) {
        let guard = 32;
        while (!lens.aligned && guard-- > 0) {
          lens.spin();
        }
        if (lens.aligned) lens.lock();
      }
      this.checkLensAlignment();
    }
    if (state.solved === true && !this.activated) {
      // Skip the REQUIRED_PUZZLES gate: the QA harness seed path is for
      // isolated apex testing. Seed lens readiness first so post-solve
      // state reads consistent with a real solve.
      if (!this.lensesReady) {
        for (const lens of this.lensRings) {
          let guard = 32;
          while (!lens.aligned && guard-- > 0) lens.spin();
          if (lens.aligned) lens.lock();
        }
        this.lensesReady = true;
      }
      this.activated = true;
      this.activate();
    }
  }

  dispose(): void {
    if (this.orreryProp) {
      this.orreryProp.dispose();
      this.orreryProp = null;
    }
    this.orreryTorus.dispose();
    this.orreryMat.dispose();
    this.orrerySphere.dispose();
    this.sphereMat.dispose();
    for (const lens of this.lensRings) lens.dispose();
    this.lensRings = [];
    this.syncPlateA.dispose();
    this.syncPlateB.dispose();
    this.exitPortal.dispose();
  }

  // ─────────────────────────────────────────────────────────────────

  private checkActivation(): void {
    if (this.activated) return;

    // All 4 prior puzzles must be solved.
    const allPriorSolved = REQUIRED_PUZZLES.every(
      (id) => this.ctx.levelState.get(id)?.solved === true,
    );
    if (!allPriorSolved) return;

    // Canon gate: all three lens rings must read their star-chart angle.
    if (!this.lensesReady) return;

    // One sync plate latched by each cat.
    if (this.syncArmed()) {
      this.activated = true;
      this.activate();
    }
  }

  /** True once the two sync plates have been latched by different cats. */
  private syncArmed(): boolean {
    return (
      (this.syncACats.has("minna") && this.syncBCats.has("basil")) ||
      (this.syncACats.has("basil") && this.syncBCats.has("minna"))
    );
  }

  private activate(): void {
    const { gameEvents: ev } = this.ctx;
    ev.emit(GameEvents.PUZZLE_SOLVED, { puzzleId: this.id });
    ev.emit(GameEvents.HUD_TOAST, {
      message: "The Orrery awakens! Constellations align!",
      color: CLOCKWORK_CANOPY.COLORS.LENS_GOLD,
    });

    // Orrery visual activation: emissive glow
    this.orreryMat.emissiveColor = Color3.FromHexString(
      CLOCKWORK_CANOPY.COLORS.LENS_GOLD,
    ).scale(0.4);
    this.sphereMat.emissiveColor = Color3.FromHexString(
      CLOCKWORK_CANOPY.COLORS.CONSTELLATION,
    ).scale(0.5);

    // Open exit portal
    this.exitPortal.open();

    // Star chart effect: brighten the constellation accent lights
    for (const starLight of this.constellationLights) {
      starLight.intensity = 3.0;
    }
  }
}
